import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal, StatusBadge, Table, SortableTh, Dropdown } from '@/components/ui';
import { Effort } from '../../../types/Effort';
import { RoutineTask } from '../../../types/RoutineTask';
import TaskEffortsTable from '../../../components/TaskEffortsTable';
import '../styles/adaptive.css';
import RoutineTaskActions from './RoutineTaskActions';
import { useResizableColumns } from '../../../hooks/useResizableColumns';
import { ResizeHandle } from '../../../components/ResizeHandle';
import { usePermissions } from '../../../hooks/usePermissions';

const ROUTINE_EFFORTS_COLUMNS = {
  type: true,
  date: true,
  employeeName: true,
  description: true,
  accomplishment: true,
  quantity: true,
  rate: true,
  fuelSurcharge: true,
  totalCost: true,
  comments: true
} as const;

// Add new interface for modal props
interface EffortModalProps {
  show: boolean;
  onHide: () => void;
  task: RoutineTask;
  canToggleVehicleFuel: boolean;
  onVehicleFuelEffortPatched?: (effort: Effort) => void;
}

interface RoutineWorkTableProps {
  tasks: RoutineTask[];
  onTaskAction: (action: string, task: RoutineTask) => void;
  canCloneTask?: boolean;
  onTaskSelect: (taskIds: number[]) => void;
  onBulkAction: (action: string, tasks: RoutineTask[]) => void;
  selectedTasks: number[];
  onTaskClick?: (task: RoutineTask) => void;
  showBulkActions?: boolean;
  showAsset?: boolean;
  showQuantity?: boolean;
  canReopenTask?: boolean;
  onVehicleFuelEffortPatched?: (effort: Effort) => void;
}

// Update EffortModal component
const EffortModal: React.FC<EffortModalProps> = ({
  show,
  onHide,
  task,
  canToggleVehicleFuel,
  onVehicleFuelEffortPatched
}) => {
  return (
    // autoHeight: this modal is the tablet path for viewing efforts (screenSize === 'tablet'
    // opens it instead of expanding the row in place). Without it, `lg` gets the standard fixed
    // 85vh box - grouping efforts by type, the last group ("Vehicle" sorts after Labour/Material/
    // Other alphabetically) landed right at that boundary, so it read as truncated instead of
    // needing a scroll. Content-sizing removes the fixed ceiling. Matches the equivalent modal on
    // the Quantified Tasks list, which already does this.
    <Modal show={show} onHide={onHide} size="lg" centered autoHeight>
      <Modal.Header closeButton onHide={onHide}>
        <Modal.Title>Effort Details - Task {task.taskNumber || task.id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <TaskEffortsTable
          efforts={task.efforts || []}
          columns={{ ...ROUTINE_EFFORTS_COLUMNS }}
          canToggleVehicleFuel={canToggleVehicleFuel}
          onVehicleFuelEffortPatched={onVehicleFuelEffortPatched}
        />
      </Modal.Body>
    </Modal>
  );
};

const RoutineWorkTable: React.FC<RoutineWorkTableProps> = ({ 
  tasks, 
  onTaskAction,
  onTaskSelect,
  onBulkAction,
  selectedTasks,
  onTaskClick,
  canReopenTask = true,
  canCloneTask = false,
  showBulkActions = true,
  showAsset = false,
  showQuantity = false,
  onVehicleFuelEffortPatched: onVehicleFuelEffortPatchedFromParent
}) => {
  const { hasPermission } = usePermissions();
  const canToggleVehicleFuel = hasPermission('quantified:task_edit');
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [isTableLoaded, setIsTableLoaded] = useState(false);
  const [selectedTaskForModal, setSelectedTaskForModal] = useState<RoutineTask | null>(null);

  const routineTaskMatchesEffort = (t: RoutineTask, effort: Effort) =>
    effort.taskId != null && (t.id === effort.taskId || t.taskId === effort.taskId);

  const handleVehicleFuelEffortPatched = useCallback(
    (effort: Effort) => {
      setSelectedTaskForModal((prev) => {
        if (!prev || !routineTaskMatchesEffort(prev, effort)) return prev;
        return {
          ...prev,
          efforts: (prev.efforts || []).map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
        };
      });
      onVehicleFuelEffortPatchedFromParent?.(effort);
    },
    [onVehicleFuelEffortPatchedFromParent]
  );

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const stickyScrollbarRef = useRef<HTMLDivElement>(null);
  const isScrollingSyncRef = useRef(false);
  const scrollSyncRafRef = useRef<number | null>(null);
  const lastScrollSourceRef = useRef<'table' | 'bar' | null>(null);
  const [stickyScrollWidth, setStickyScrollWidth] = useState(0);
  // The shared <Table> primitive is a plain function component (no forwardRef), so it can't take
  // a ref directly the way the old hand-rolled <table> did. It still renders a single <table> as
  // the wrapper's only child, so the ResizeObserver below just reaches in for it instead.
  const getTableEl = useCallback(
    (): HTMLTableElement | null => tableWrapperRef.current?.querySelector('table') ?? null,
    []
  );

  const RESIZABLE_COLUMN_IDS = ['taskNumber', 'date', 'name', 'description', 'asset', 'quantity', 'activity', 'status', 'actions'];
  const defaultWidths: Record<string, string> = {
    taskNumber: '80px',
    date: '120px',
    name: '300px',
    description: '300px',
    asset: '240px',
    quantity: '120px',
    activity: '240px',
    status: '120px',
    actions: '80px'
  };
  const { getColumnWidth, getColumnWidthPx, startResize } = useResizableColumns(
    RESIZABLE_COLUMN_IDS,
    { defaultWidths }
  );

  // Simplified device detection
  const checkScreenSize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Update orientation
    setOrientation(width > height ? 'landscape' : 'portrait');
    
    // Determine screen size
    if (width <= 768) {
      setScreenSize('mobile');
    } else if (width <= 1200) {
      setScreenSize('tablet');
    } else {
      setScreenSize('desktop');
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkScreenSize();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [checkScreenSize]);

  useEffect(() => {
    // Set table as loaded after a short delay to trigger animations
    const timer = setTimeout(() => {
      setIsTableLoaded(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Measure table wrapper scroll width for sticky scrollbar
  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    const table = getTableEl();
    if (!wrapper || !table) return;
    const updateStickyWidth = () => setStickyScrollWidth(wrapper.scrollWidth);
    updateStickyWidth();
    const ro = new ResizeObserver(updateStickyWidth);
    ro.observe(table);
    return () => ro.disconnect();
  }, [tasks.length, getColumnWidth, getTableEl]);

  const runScrollSync = useCallback(() => {
    const src = lastScrollSourceRef.current;
    const tableEl = tableWrapperRef.current;
    const barEl = stickyScrollbarRef.current;
    if (src === 'table' && barEl && tableEl) {
      isScrollingSyncRef.current = true;
      barEl.scrollLeft = tableEl.scrollLeft;
      requestAnimationFrame(() => { isScrollingSyncRef.current = false; });
    } else if (src === 'bar' && tableEl && barEl) {
      isScrollingSyncRef.current = true;
      tableEl.scrollLeft = barEl.scrollLeft;
      requestAnimationFrame(() => { isScrollingSyncRef.current = false; });
    }
  }, []);

  const handleTableWrapperScroll = useCallback(() => {
    if (isScrollingSyncRef.current) return;
    lastScrollSourceRef.current = 'table';
    if (scrollSyncRafRef.current === null) {
      scrollSyncRafRef.current = requestAnimationFrame(() => {
        scrollSyncRafRef.current = null;
        runScrollSync();
      });
    }
  }, [runScrollSync]);

  const handleStickyScroll = useCallback(() => {
    if (isScrollingSyncRef.current) return;
    lastScrollSourceRef.current = 'bar';
    if (scrollSyncRafRef.current === null) {
      scrollSyncRafRef.current = requestAnimationFrame(() => {
        scrollSyncRafRef.current = null;
        runScrollSync();
      });
    }
  }, [runScrollSync]);

  // Debug tasks prop when it changes
  useEffect(() => {
    if (tasks.length > 0) {
      console.log('RoutineWorkTable received tasks:', tasks.length);
      console.log('First task structure sample:', {
        id: tasks[0].id,
        taskId: tasks[0].taskId,
        taskNumber: tasks[0].taskNumber,
        has_activities: Array.isArray(tasks[0].activities),
        activities_length: Array.isArray(tasks[0].activities) ? tasks[0].activities.length : 0,
        first_activity: Array.isArray(tasks[0].activities) && tasks[0].activities.length > 0 
          ? tasks[0].activities[0] : null,
        has_taskLocations: Array.isArray(tasks[0].taskLocations),
        locations_length: Array.isArray(tasks[0].taskLocations) ? tasks[0].taskLocations.length : 0,
        first_location: Array.isArray(tasks[0].taskLocations) && tasks[0].taskLocations.length > 0
          ? tasks[0].taskLocations[0] : null,
        has_efforts: Array.isArray(tasks[0].efforts)
      });
    }
  }, [tasks]);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  // Function to truncate text with ellipsis
  const truncateText = (text: string, maxLength: number = 20) => {
    if (!text) return '-';
    
    // Adjust maxLength based on device and orientation
    let effectiveMaxLength = maxLength;
    
    if (screenSize === 'tablet') {
      effectiveMaxLength = orientation === 'portrait' ? 15 : 25;
    }
    
    return text.length > effectiveMaxLength ? text.substring(0, effectiveMaxLength) + '...' : text;
  };

  const formatEffortDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'Unknown';
    try {
      // Handle timezone issues by treating the date as local to avoid UTC conversion problems
      let date: Date;
      
      if (dateStr.includes('T') && (dateStr.includes('Z') || dateStr.includes('+'))) {
        // Full ISO string with timezone - parse normally but convert to local
        date = new Date(dateStr);
      } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // YYYY-MM-DD format - treat as local date to avoid timezone shift
        const [year, month, day] = dateStr.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else if (dateStr.includes('T') && !dateStr.includes('Z') && !dateStr.includes('+')) {
        // ISO string without timezone - treat as local to avoid UTC conversion
        const [datePart, timePart] = dateStr.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds] = timePart.split(':').map(Number);
        date = new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0);
      } else {
        // Other formats - parse normally
        date = new Date(dateStr);
      }
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateStr);
        return 'Unknown';
      }
      
      // Use toLocaleDateString with explicit options to ensure consistent formatting
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Unknown';
    }
  };

  // Helper function to get the earliest work completion date for a task
  const getTaskWorkDate = (task: RoutineTask): string => {
    // Priority order: startDate > earliest effort date > completion date > identified date > created date
    if (task.startDate) return task.startDate;
    
    if (task.efforts && task.efforts.length > 0) {
      // Get the earliest effort date (first work completed)
      const effortDates = task.efforts
        .map(effort => effort.date)
        .filter(date => date)
        .sort((a, b) => new Date(a!).getTime() - new Date(b!).getTime());
      
      if (effortDates.length > 0) {
        return effortDates[0]!;
      }
    }
    
    // Fallback to task dates
    if (task.completionDate) return task.completionDate;
    if (task.identifiedDate) return task.identifiedDate;
    if (task.createdAt) return task.createdAt;
    
    return '';
  };

  const handleSelectTask = (taskId: number) => {
    const newSelected = new Set(selectedTasks || []);
    if (selectedTasks && selectedTasks.includes(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    onTaskSelect?.(Array.from(newSelected));
  };

  const handleSelectAll = () => {
    if (selectedTasks && selectedTasks.length === tasks.length) {
      onTaskSelect?.([]);
    } else {
      onTaskSelect?.(tasks.map(task => task.id));
    }
  };

  const handleBulkAction = (action: string) => {
    const selectedTaskObjects = tasks.filter(task => selectedTasks.includes(task.id));
    onBulkAction(action, selectedTaskObjects);
    // Dropdown (Headless UI Menu) closes itself on item activation - no open-state to reset here.
  };

  // Previously getStatusBadgeClass/getStatusDisplay: a local status->colour map that, unlike the
  // shared one, coloured ACTIVE_MARKED_AS_COMPLETE 'success' (green) and rendered it as a plain
  // "Active" badge plus a separate green check icon - an active task reading as already finished,
  // and differently from the identical status on the Quantified Tasks list. StatusBadge is the
  // single source of truth for status colour/wording (see utils/statusUtils), so this task and
  // that one now render identically wherever they're shown.
  const toggleRowExpansion = (taskId: number, task: RoutineTask) => {
    if (screenSize === 'tablet') {
      setSelectedTaskForModal(task);
    } else {
      setExpandedRows(prev => 
        prev.includes(taskId) 
          ? prev.filter(id => id !== taskId)
          : [...prev, taskId]
      );
    }
  };

  const calculateEffortTotal = (efforts: any[]) => {
    return efforts.reduce((sum, effort) => sum + (effort.quantity * effort.rate), 0);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // getSortIcon was replaced by SortableTh, which renders the same icon set itself from
  // sortField/sortDirection (passed through as activeKey/direction) - no separate helper needed.
  const renderEffortDetails = (task: RoutineTask) => {
    // Calculate dynamic colspan based on visible columns
    let colSpanCount = 8; // Base columns: checkbox, task#, date, name, description, activity, status, actions
    if (showAsset) colSpanCount += 1;
    if (showQuantity) colSpanCount += 1;
    
    return (
      <tr>
        <td colSpan={colSpanCount}>
          <div className="tw-p-3 tw-bg-surface-alt tw-border tw-border-line tw-rounded">
            <TaskEffortsTable 
              efforts={task.efforts || []}
              columns={{ ...ROUTINE_EFFORTS_COLUMNS }}
              canToggleVehicleFuel={canToggleVehicleFuel}
              onVehicleFuelEffortPatched={handleVehicleFuelEffortPatched}
            />
          </div>
        </td>
      </tr>
    );
  };

  // Sort tasks based on current sort field and direction
  const sortedTasks = [...tasks].sort((a, b) => {
    if (!sortField) return 0;

    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'taskNumber':
        aValue = a.taskNumber || '';
        bValue = b.taskNumber || '';
        break;
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        break;
      case 'description':
        aValue = a.description || '';
        bValue = b.description || '';
        break;
      case 'asset':
        aValue = a.taskLocations?.[0]?.asset?.name || '';
        bValue = b.taskLocations?.[0]?.asset?.name || '';
        break;
      case 'activity':
        aValue = a.activities?.[0]?.activityName || '';
        bValue = b.activities?.[0]?.activityName || '';
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      case 'priority':
        aValue = a.priority || '';
        bValue = b.priority || '';
        break;
      case 'quantity':
        aValue = a.completionAccomplishment || 0;
        bValue = b.completionAccomplishment || 0;
        break;
      case 'date':
        aValue = getTaskWorkDate(a) ? new Date(getTaskWorkDate(a)).getTime() : 0;
        bValue = getTaskWorkDate(b) ? new Date(getTaskWorkDate(b)).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Use sortedTasks directly since pagination is handled by the parent component

  return (
    <div>
      {showBulkActions && selectedTasks && selectedTasks.length > 0 && (
        <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-content-muted">
            {selectedTasks.length} task{selectedTasks.length > 1 ? 's' : ''} selected
          </span>
          {/* Was a hand-rolled <ul>/<button> menu with its own open state - the one bulk-action
              dropdown in the app that didn't look like every other menu. Dropdown gives it the
              same white surface, shadow-lg and hover:bg-surface-hover row highlight. */}
          <Dropdown>
            <Dropdown.Toggle>Bulk Actions</Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleBulkAction('cancel')}>Cancel Selected</Dropdown.Item>
              <Dropdown.Item onClick={() => handleBulkAction('set-active')}>Set to Active</Dropdown.Item>
              <Dropdown.Item onClick={() => handleBulkAction('duplicate')}>Duplicate Selected</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )}

      {stickyScrollWidth > 0 && (
        <div
          ref={stickyScrollbarRef}
          onScroll={handleStickyScroll}
          className="tw-hidden md:tw-block"
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            height: 14,
            backgroundColor: 'var(--bs-body-bg, #fff)',
            borderBottom: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ width: stickyScrollWidth, height: 1 }} />
        </div>
      )}
      {/* Mobile (< md): stacked cards — the wide routine table doesn't fit a phone.
          Full table returns at md+. */}
      <div className="tw-flex tw-flex-col tw-gap-2 tw-p-2 md:tw-hidden">
        {sortedTasks.length === 0 ? (
          <div className="tw-py-6 tw-text-center tw-text-content-muted">No routine tasks found</div>
        ) : (
          sortedTasks.map((task) => {
            const hasEfforts = !!task.efforts?.some((e) => e.quantity || e.rate);
            const activity =
              Array.isArray(task.activities) && task.activities.length > 0
                ? `#${task.activities[0].activityNumber ?? ''} ${task.activities[0].activityName ?? ''}`.trim()
                : '-';
            return (
              <div
                key={`m-${task.id}`}
                className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3 tw-shadow-sm"
              >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                    <input
                      type="checkbox"
                      checked={selectedTasks && selectedTasks.includes(task.id)}
                      onChange={() => handleSelectTask(task.id)}
                      className="tw-h-4 tw-w-4 tw-shrink-0 tw-rounded tw-border-line-input tw-text-primary"
                    />
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); onTaskClick?.(task); }}
                      className="tw-truncate tw-font-bold tw-text-primary tw-no-underline"
                    >
                      {task.taskNumber || `T-${task.id}`}
                    </a>
                  </div>
                  <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                    <StatusBadge status={task.status} />
                    <RoutineTaskActions
                      task={task}
                      onAction={(action) => onTaskAction(action, task)}
                      canReopenTask={canReopenTask}
                      canCloneTask={canCloneTask}
                    />
                  </div>
                </div>
                {task.name && (
                  <div className="tw-mt-1 tw-break-words tw-font-medium tw-text-content">{task.name}</div>
                )}
                {task.description && (
                  <div className="tw-mt-0.5 tw-break-words tw-text-sm tw-text-content-secondary">
                    {truncateText(task.description, 120)}
                  </div>
                )}
                <div className="tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1.5 tw-text-sm">
                  <div className="tw-min-w-0">
                    <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-content-muted">Date</div>
                    <div className="tw-text-content">{formatEffortDate(getTaskWorkDate(task))}</div>
                  </div>
                  <div className="tw-min-w-0">
                    <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-content-muted">Activity</div>
                    <div className="tw-truncate tw-text-content" title={activity}>{activity}</div>
                  </div>
                  {showQuantity && (
                    <div className="tw-min-w-0">
                      <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-content-muted">Quantity</div>
                      <div className="tw-tabular-nums tw-text-content">
                        {typeof task.completionAccomplishment === 'number'
                          ? task.completionAccomplishment.toFixed(2)
                          : '-'}
                      </div>
                    </div>
                  )}
                </div>
                {hasEfforts && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="tw-mt-3 tw-w-full"
                    onClick={() => setSelectedTaskForModal(task)}
                    title="View efforts"
                  >
                    <i className="bi bi-list-ul tw-mr-1" />
                    View efforts
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>

      <div
        ref={tableWrapperRef}
        className="tw-hidden md:tw-block tw-w-full tw-overflow-x-auto"
        style={{ overflowX: 'auto' }}
        onScroll={handleTableWrapperScroll}
      >
        {/* Routed through the shared Table primitive (striped + hover, the app-standard header
            weight) instead of a hand-copied class list - was the "inconsistent header weight"
            report. Sort/selection/expansion stay hand-rolled: this table's local client-side sort
            plus resizable columns is too entangled for a DataTable migration (see the migration
            note in the PR/report). */}
        <Table striped hover style={{ tableLayout: 'fixed', minWidth: '100%', width: 'max-content' }}>
        <thead className="table-sticky-header">
          <tr>
            <th style={{ width: '40px' }}>
              <input
                type="checkbox"
                className="tw-h-4 tw-w-4 tw-rounded tw-border-line-input tw-text-primary"
                checked={selectedTasks && selectedTasks.length === tasks.length && tasks.length > 0}
                onChange={handleSelectAll}
              />
            </th>
            <SortableTh
              style={{ width: getColumnWidth('taskNumber'), position: 'relative' }}
              label="Task #"
              sortKey="taskNumber"
              activeKey={sortField}
              direction={sortDirection}
              onSort={handleSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="taskNumber" currentWidthPx={getColumnWidthPx('taskNumber')} onResizeStart={startResize} />
              )}
            </SortableTh>
            <SortableTh
              style={{ width: getColumnWidth('date'), position: 'relative' }}
              label="Start Date"
              sortKey="date"
              activeKey={sortField}
              direction={sortDirection}
              onSort={handleSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="date" currentWidthPx={getColumnWidthPx('date')} onResizeStart={startResize} />
              )}
            </SortableTh>
            <SortableTh
              style={{ width: getColumnWidth('name'), position: 'relative' }}
              label="Name"
              sortKey="name"
              activeKey={sortField}
              direction={sortDirection}
              onSort={handleSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="name" currentWidthPx={getColumnWidthPx('name')} onResizeStart={startResize} />
              )}
            </SortableTh>
            <SortableTh
              style={{ width: getColumnWidth('description'), position: 'relative' }}
              label="Description"
              sortKey="description"
              activeKey={sortField}
              direction={sortDirection}
              onSort={handleSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="description" currentWidthPx={getColumnWidthPx('description')} onResizeStart={startResize} />
              )}
            </SortableTh>
            {showAsset && (
              <SortableTh
                style={{ width: getColumnWidth('asset'), position: 'relative' }}
                label="Asset"
                sortKey="asset"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
              >
                {screenSize === 'desktop' && (
                  <ResizeHandle columnId="asset" currentWidthPx={getColumnWidthPx('asset')} onResizeStart={startResize} />
                )}
              </SortableTh>
            )}
            {showQuantity && (
              <SortableTh
                style={{ width: getColumnWidth('quantity'), position: 'relative' }}
                label="Quantity"
                sortKey="quantity"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
              >
                {screenSize === 'desktop' && (
                  <ResizeHandle columnId="quantity" currentWidthPx={getColumnWidthPx('quantity')} onResizeStart={startResize} />
                )}
              </SortableTh>
            )}
            <SortableTh
              style={{ width: getColumnWidth('activity'), position: 'relative' }}
              label="Activity"
              sortKey="activity"
              activeKey={sortField}
              direction={sortDirection}
              onSort={handleSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="activity" currentWidthPx={getColumnWidthPx('activity')} onResizeStart={startResize} />
              )}
            </SortableTh>
            <SortableTh
              style={{ width: getColumnWidth('status'), position: 'relative' }}
              label="Status"
              sortKey="status"
              activeKey={sortField}
              direction={sortDirection}
              onSort={handleSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="status" currentWidthPx={getColumnWidthPx('status')} onResizeStart={startResize} />
              )}
            </SortableTh>
            {/* Width stays user-resizable (getColumnWidth), not the fixed ACTIONS_COL_WIDTH other
                tables use - this column is wired into the same useResizableColumns state as every
                other column here, and a fixed width would desync it from its own resize handle. */}
            <th style={{ width: getColumnWidth('actions'), position: 'relative' }}>
              Actions
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="actions" currentWidthPx={getColumnWidthPx('actions')} onResizeStart={startResize} />
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedTasks.map((task) => {
            const isExpanded = expandedRows.includes(task.id);
            const hasEfforts = task.efforts?.length ?? 0 > 0; 

            // Get the first activity name and number using explicit null checking
            const activityDisplay = (() => {
              try {
                if (Array.isArray(task.activities) && task.activities.length > 0) {
                  const firstActivity = task.activities[0];
                  if (firstActivity && typeof firstActivity.activityName === 'string') {
                    const activityNumber = firstActivity.activityNumber || '';
                    const activityName = firstActivity.activityName || '';
                    return activityNumber ? `#${activityNumber} - ${activityName}` : activityName;
                  }
                  return 'No Activity Name';
                }
                return 'No Activity';
              } catch (err) {
                console.error('Error accessing activity name:', err);
                return 'No Activity';
              }
            })();
            
            // Get the first asset name with proper null checking
            const assetName = (() => {
              try {
                if (Array.isArray(task.taskLocations) && task.taskLocations.length > 0) {
                  const firstLocation = task.taskLocations[0];
                  if (firstLocation && firstLocation.asset) {
                    return typeof firstLocation.asset.name === 'string' 
                      ? firstLocation.asset.name 
                      : 'Unnamed Asset';
                  }
                }
                return 'No Asset';
              } catch (err) {
                console.error('Error accessing asset name:', err);
                return 'No Asset';
              }
            })();
            
            // Get completion quantity safely
            const quantity = (() => {
              try {
                const value = task.completionAccomplishment;
                return typeof value === 'number' ? value : 0;
              } catch (err) {
                console.error('Error accessing completion quantity:', err);
                return 0;
              }
            })();

            return (
              <React.Fragment key={task.id}>
                <tr>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedTasks && selectedTasks.includes(task.id)}
                      onChange={() => handleSelectTask(task.id)}
                      className="tw-h-4 tw-w-4 tw-rounded tw-border-line-input tw-text-primary"
                    />
                  </td>
                  <td>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onTaskClick?.(task);
                      }}
                      className="tw-text-primary tw-no-underline"
                      style={{ 
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      {task.taskNumber || `T-${task.id}`}
                    </a>
                  </td>
                  <td>
                    {formatEffortDate(getTaskWorkDate(task))}
                  </td>
                  <td>
                    <div title={task.name || ''}>
                      {(() => {
                        const result = truncateText(task.name || '', 50);
                        return result;
                      })()}
                    </div>
                  </td>
                  <td>
                    <div title={task.description || ''}>
                      {(() => {
                        const result = truncateText(task.description || '', 50);
                        return result;
                      })()}
                    </div>
                  </td>
                  {showAsset && (
                    <td title={assetName}>
                      {truncateText(assetName, 30)}
                    </td>
                  )}
                  {showQuantity && (
                    <td>{quantity.toFixed(2)}</td>
                  )}
                  <td title={activityDisplay}>
                    {truncateText(activityDisplay, 30)}
                  </td>
                  <td>
                    <StatusBadge status={task.status} />
                  </td>
                  <td>
                    {/* Was a standalone chevron <Button> beside RoutineTaskActions, from this call
                        site and the mobile one below - two independently-placed controls that
                        never lined up ("action dots are misaligned when one task has expandable
                        items"). RowActions' expander renders the chevron and the kebab as one
                        control group at a fixed width. */}
                    <RoutineTaskActions
                      task={task}
                      onAction={(action) => onTaskAction(action, task)}
                      canReopenTask={canReopenTask}
                      canCloneTask={canCloneTask}
                      expander={
                        task.efforts && task.efforts.some(e => e.quantity || e.rate)
                          ? { expanded: isExpanded, onToggle: () => toggleRowExpansion(task.id, task), title: 'View Efforts' }
                          : undefined
                      }
                    />
                  </td>
                </tr>
                {isExpanded && screenSize !== 'tablet' && renderEffortDetails(task)}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      </div>

      <div style={{ height: '50px' }}></div>

      {/* Add modal for tablet effort details */}
      {selectedTaskForModal && (
        <EffortModal
          show={!!selectedTaskForModal}
          onHide={() => setSelectedTaskForModal(null)}
          task={selectedTaskForModal}
          canToggleVehicleFuel={canToggleVehicleFuel}
          onVehicleFuelEffortPatched={handleVehicleFuelEffortPatched}
        />
      )}
    </div>
  );
};

export default RoutineWorkTable; 